import { useMemo } from 'react';

import { TaxonomyType } from '@revelio/data-access';
import type { Item } from '@revelio/iso-utility';

import type { TreeData } from '../../components/tree/useTreeApi';
import { RICS_TAXONOMY_ID } from '../constants';
import { applyDepthCompoundIds } from '../utils/applyDepthCompoundIds';
import { useTaxonomyTree } from './useTaxonomyTree';

interface UseCompanyPlaceholderTreeOptions {
  /** Optional filter applied to company leaf nodes before returning the tree. */
  companyFilterFn?: (item: Item) => boolean;
  /** Pause the underlying taxonomyTree query (e.g. until placeholders are shown). */
  pause?: boolean;
}

const pruneTree = (
  nodes: TreeData[],
  filterFn: (item: Item) => boolean
): TreeData[] =>
  nodes.reduce<TreeData[]>((acc, node) => {
    if (node.children) {
      const filteredChildren = pruneTree(node.children, filterFn);
      if (filteredChildren.length > 0) {
        acc.push({ ...node, children: filteredChildren });
      }
    } else if (filterFn(node as unknown as Item)) {
      acc.push(node);
    }
    return acc;
  }, []);

/**
 * Fetches the RICS k10→k50→company taxonomy tree and returns it as a
 * depth-compound-id `TreeData[]` suitable for the company browse-mode picker.
 * Sorts "Other" to the bottom of the root level.
 *
 * An optional `companyFilterFn` can be provided to prune leaf nodes (and
 * empty parent groups) before returning.
 */
export const useCompanyPlaceholderTree = (
  options?: UseCompanyPlaceholderTreeOptions
): TreeData[] => {
  const { treeData, fetching } = useTaxonomyTree({
    taxonomyType: TaxonomyType.Rics,
    taxonomyId: RICS_TAXONOMY_ID,
    levels: [1, 2, 4],
    pause: options?.pause,
  });

  const companyFilterFn = options?.companyFilterFn;

  return useMemo(() => {
    if (fetching || treeData.length === 0) return [];

    // Prune on raw node ids first (companyFilterFn matches raw rcids), then
    // rewrite to depth-compound ids for the browse-mode picker.
    const pruned = companyFilterFn
      ? pruneTree(treeData, companyFilterFn)
      : treeData;
    const withCompoundIds = applyDepthCompoundIds(pruned);

    withCompoundIds.sort((a, b) => {
      const aIsOther = a.name === 'Other';
      const bIsOther = b.name === 'Other';
      if (aIsOther !== bIsOther) return aIsOther ? 1 : -1;
      return 0;
    });

    return withCompoundIds;
  }, [treeData, fetching, companyFilterFn]);
};
